/**
 * lib/reconChanges.ts
 * ------------------------------------------------------------------
 * FINANCE — the record of every portal entry undone to match Pastel, so
 * each can be put back (Pastel Check → "Changes made to match Pastel").
 *
 * THE RULE (18 Oct 2026, from the school): Pastel is the record. A
 * payment in the portal with no Pastel receipt is reversed; an invoice
 * raised by hand that Pastel never charged is voided. Whoever made the
 * entry may know better, so nothing is lost and every change is one click
 * from undone: the reversal is a ledger entry (the original payment kept,
 * flagged `reversed`), the void a status — and this record says what was
 * done, to what, and who had made the entry in the first place.
 *
 * Written by seed/align-to-pastel.ts (the first, bulk run) and by the
 * Pastel Check's own Reverse and Void buttons (features/finance/
 * reconFixWrite). Kept, never deleted: an undo marks it undone.
 * HIGH-RISK-CHANGES.md §13.
 * ------------------------------------------------------------------ */

export const RECON_CHANGES_COLLECTION = 'financeReconChanges';

/** reversePayment / voidInvoice undo a portal entry Pastel lacks; addPupil /
 *  linkPupil bring a Pastel account into the portal (Oct 2026). */
export type ReconChangeKind = 'reversePayment' | 'voidInvoice' | 'addPupil' | 'linkPupil';

export interface ReconChange {
  id: string;
  /** 'align-2026-10-18' for the bulk run; 'card' for a button on the Pastel Check. */
  batch: string;
  kind: ReconChangeKind;
  studentId: string;
  studentName?: string;
  pastelCode?: string;
  amount: number;
  /** The invoice the payment was against, or the invoice voided. */
  invoiceId: string;
  invoiceNo?: string;
  /** reversePayment: the original payment, and the reversal written against it. */
  paymentId?: string;
  reversalPaymentId?: string;
  receiptNo?: string;
  /** Who made the portal entry, and when — the person to ask. */
  originalBy?: string;
  originalAt?: string;
  /** voidInvoice: the status to put back. */
  beforeStatus?: string;
  reason: string;
  pastelFile?: string;
  at: string;
  byId: string;
  byName: string;
  status: 'applied' | 'undone';
  undoneAt?: string;
  undoneById?: string;
  undoneByName?: string;
  /** reversePayment undone: the payment written to put the money back. */
  reinstatedPaymentId?: string;
}

/** One line on what was done, for the list. */
export function describeChange(c: Pick<ReconChange, 'kind' | 'receiptNo' | 'invoiceNo' | 'amount' | 'pastelCode'>): string {
  const p = `P${Math.abs(c.amount).toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const sign = c.amount < 0 ? ' in credit' : '';
  switch (c.kind) {
    case 'reversePayment': return `Reversed payment ${c.receiptNo ?? ''} · ${p}`.replace('  ', ' ');
    case 'voidInvoice': return `Voided invoice ${c.invoiceNo ?? ''} · ${p}`.replace('  ', ' ');
    case 'addPupil': return `Added from Pastel (${c.pastelCode ?? ''}) · opening balance ${p}${sign}`;
    case 'linkPupil': return `Linked Pastel account ${c.pastelCode ?? ''}`;
    default: return '';
  }
}
